
'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, BookOpen, Quote, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { NewProjectDialog } from '@/domains/projects/components/new-project-dialog';
import { DuplicateProjectDialog } from '@/domains/projects/components/duplicate-project-dialog';
import { DeleteProjectDialog } from '@/domains/projects/components/delete-project-dialog';
import { TransferProjectDialog } from '@/domains/projects/components/transfer-project-dialog';
import { GroupNameDialog } from '@/domains/projects/components/group-name-dialog';
import { ProjectGroupContainer } from '@/domains/projects/components/project-group';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth-provider';
import { Project } from '@/domains/projects/schemas';
import {
  ProjectGroupsLayout,
  toGroupLayoutId,
  isGroupLayoutItem,
  getGroupId,
} from '@/domains/projects/schemas/project-groups';
import { getProjectsForUser } from '@/domains/projects/services/project-service';
import { deleteProject as deleteProjectFromServer } from '@/domains/projects/services/delete-project';
import {
  getProjectGroupsLayout,
  createGroup,
  renameGroup,
  deleteGroup,
  addProjectToGroup,
  removeProjectFromGroup,
  reorderProjectsInGroup,
  reorderLayout,
  toggleGroupCollapsed,
} from '@/domains/projects/services/project-groups-service';
import { ProjectCard } from '@/domains/projects/components/project-card';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';


const inspirationalQuotes = [
    {
        quote: "There is no greater agony than bearing an untold story inside you.",
        author: "Maya Angelou"
    },
    {
        quote: "You can't use up creativity. The more you use, the more you have.",
        author: "Maya Angelou"
    },
    {
        quote: "Start writing, no matter what. The water does not flow until the faucet is turned on.",
        author: "Louis L'Amour"
    },
    {
        quote: "You don't start out writing good stuff. You start out writing crap and thinking it's good stuff, and then gradually you get better at it.",
        author: "Octavia E. Butler"
    },
    {
        quote: "A writer is someone for whom writing is more difficult than it is for other people.",
        author: "Thomas Mann"
    },
    {
        quote: "Tears are the words that need to be written.",
        author: "Paulo Coelho"
    }
];

export default function Home() {
  // --- Dialog State ---
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [isDuplicateProjectOpen, setIsDuplicateProjectOpen] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState<Partial<Project> | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Partial<Project> | null>(null);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [projectToTransfer, setProjectToTransfer] = useState<Partial<Project> | null>(null);

  // --- Group Dialog State ---
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [groupDialogMode, setGroupDialogMode] = useState<'create' | 'rename'>('create');
  const [groupToRename, setGroupToRename] = useState<{ id: string; name: string } | null>(null);
  const [newProjectTargetGroupId, setNewProjectTargetGroupId] = useState<string | null>(null);

  // --- Data State ---
  const [projects, setProjects] = useState<Partial<Project>[]>([]);
  const [layout, setLayout] = useState<ProjectGroupsLayout | null>(null);
  const [projectsInitialized, setProjectsInitialized] = useState(false);

  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  // --- DnD Sensors ---
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // --- Data Fetching ---
  const fetchData = useCallback(async () => {
    if (!user) {
        setProjects([]);
        setLayout(null);
        setProjectsInitialized(true);
        return;
    }
    try {
        const [userProjects, userLayout] = await Promise.all([
          getProjectsForUser(user.uid),
          getProjectGroupsLayout(user.uid),
        ]);
        setProjects(userProjects);
        setLayout(userLayout);
    } catch (error) {
        console.error('[Home Page] Failed to load data from Firestore.', error);
        toast({
            variant: 'destructive',
            title: 'Failed to load projects',
            description: 'Could not retrieve your projects from the server. Please try refreshing the page.',
        });
        setProjects([]);
    } finally {
        setProjectsInitialized(true);
    }
  }, [user, toast]);

  useEffect(() => {
    if (!authLoading) {
      fetchData();
    }
  }, [user, authLoading, fetchData]);

  const isInitialized = projectsInitialized && !authLoading;

  useEffect(() => {
    const randomQuote = inspirationalQuotes[Math.floor(Math.random() * inspirationalQuotes.length)];
    toast({
      description: (
        <div className="flex items-start gap-4">
          <Quote className="h-5 w-5 text-muted-foreground mt-1" />
          <div>
            <p className="font-medium italic">&quot;{randomQuote.quote}&quot;</p>
            <p className="text-right text-muted-foreground/80 mt-2">— {randomQuote.author}</p>
          </div>
        </div>
      ),
      duration: 8000,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Derived Data ---
  const projectMap = useMemo(() => {
    const map = new Map<string, Partial<Project>>();
    projects.forEach(p => { if (p.id) map.set(p.id, p); });
    return map;
  }, [projects]);

  const groups = useMemo(() => {
    if (!layout) return [];
    // Return groups in layoutOrder order, falling back to groups array order
    const groupMap = new Map(layout.groups.map(g => [g.id, g]));
    const ordered: typeof layout.groups = [];
    const seen = new Set<string>();

    for (const item of layout.layoutOrder) {
      if (isGroupLayoutItem(item)) {
        const gId = getGroupId(item);
        const group = groupMap.get(gId);
        if (group && !seen.has(gId)) {
          // Filter out any project IDs that no longer exist
          ordered.push({
            ...group,
            projectIds: group.projectIds.filter(pid => projectMap.has(pid)),
          });
          seen.add(gId);
        }
      }
    }
    // Add any groups not in layoutOrder
    layout.groups.forEach(g => {
      if (!seen.has(g.id)) {
        ordered.push({
          ...g,
          projectIds: g.projectIds.filter(pid => projectMap.has(pid)),
        });
      }
    });
    return ordered;
  }, [layout, projectMap]);

  const groupedProjectIds = useMemo(() => {
    const ids = new Set<string>();
    groups.forEach(g => g.projectIds.forEach(pid => ids.add(pid)));
    return ids;
  }, [groups]);

  const ungroupedProjects = useMemo(() => {
    return projects
      .filter(p => p.id && !groupedProjectIds.has(p.id))
      .sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime());
  }, [projects, groupedProjectIds]);

  const allProjects = useMemo(() => {
    if (!projects) return [];
    return [...projects].sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime());
  }, [projects]);

  // Sortable IDs for the top-level DnD (group reordering)
  const groupSortableIds = useMemo(() =>
    groups.map(g => toGroupLayoutId(g.id)),
    [groups]
  );

  // --- Project Actions ---
  const handleDeleteProject = async () => {
    if (!projectToDelete || !projectToDelete.id) return false;

    try {
        await deleteProjectFromServer(projectToDelete.id);
        toast({
          title: "Project Deleted",
          description: `"${projectToDelete.title}" has been permanently removed.`,
          variant: 'destructive'
        });
        await fetchData();
        return true;
    } catch (error) {
        toast({
            variant: 'destructive',
            title: 'Deletion Failed',
            description: 'Could not delete the project. Please try again.',
        });
        return false;
    }
  }

  const handleOpenDuplicateDialog = (project: Partial<Project>) => {
    setProjectToDuplicate(project);
    setIsDuplicateProjectOpen(true);
  };

  const handleOpenDeleteDialog = (project: Partial<Project>) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const handleOpenTransferDialog = (project: Partial<Project>) => {
    setProjectToTransfer(project);
    setIsTransferDialogOpen(true);
  };

  const handleTransferred = () => {
    toast({
      title: 'Project Transferred',
      description: `"${projectToTransfer?.title}" has been transferred successfully.`,
    });
    fetchData();
  };

  // --- Group Actions ---
  const handleCreateGroup = async (name: string) => {
    if (!user) return;
    try {
      const updated = await createGroup(user.uid, name, layout);
      setLayout(updated);
      toast({ title: 'Group Created', description: `"${name}" has been created.` });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to create group', description: 'Please try again.' });
    }
  };

  const handleRenameGroup = async (name: string) => {
    if (!user || !layout || !groupToRename) return;
    try {
      const updated = await renameGroup(user.uid, groupToRename.id, name, layout);
      setLayout(updated);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to rename group', description: 'Please try again.' });
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!user || !layout) return;
    try {
      const updated = await deleteGroup(user.uid, groupId, layout);
      setLayout(updated);
      toast({ title: 'Group Deleted', description: 'Projects have been moved to ungrouped.' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to delete group', description: 'Please try again.' });
    }
  };

  const handleAddToGroup = async (projectId: string, groupId: string) => {
    if (!user || !layout) return;
    try {
      const updated = await addProjectToGroup(user.uid, projectId, groupId, layout);
      setLayout(updated);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to add project to group', description: 'Please try again.' });
    }
  };

  const handleRemoveFromGroup = async (projectId: string, groupId: string) => {
    if (!user || !layout) return;
    try {
      const updated = await removeProjectFromGroup(user.uid, projectId, groupId, layout);
      setLayout(updated);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to remove project from group', description: 'Please try again.' });
    }
  };

  const handleReorderInGroup = async (groupId: string, newProjectIds: string[]) => {
    if (!user || !layout) return;
    // Optimistic update
    setLayout(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        groups: prev.groups.map(g =>
          g.id === groupId ? { ...g, projectIds: newProjectIds } : g
        ),
      };
    });
    try {
      await reorderProjectsInGroup(user.uid, groupId, newProjectIds, layout);
    } catch (error) {
      // Rollback by refetching
      toast({ variant: 'destructive', title: 'Failed to reorder', description: 'Please try again.' });
      fetchData();
    }
  };

  const handleToggleCollapse = async (groupId: string) => {
    if (!user || !layout) return;
    // Optimistic update
    setLayout(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        groups: prev.groups.map(g =>
          g.id === groupId ? { ...g, isCollapsed: !g.isCollapsed } : g
        ),
      };
    });
    try {
      await toggleGroupCollapsed(user.uid, groupId, layout);
    } catch {
      // Non-critical, silently fail
    }
  };

  const handleOpenRenameDialog = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    setGroupToRename({ id: group.id, name: group.name });
    setGroupDialogMode('rename');
    setIsGroupDialogOpen(true);
  };

  const handleOpenCreateGroupDialog = () => {
    setGroupToRename(null);
    setGroupDialogMode('create');
    setIsGroupDialogOpen(true);
  };

  // --- Top-Level DnD (Group Reordering) ---
  const handleGroupDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !user || !layout) return;

    const oldIndex = groupSortableIds.indexOf(active.id as string);
    const newIndex = groupSortableIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(groupSortableIds, oldIndex, newIndex);

    // Optimistic update: rebuild layoutOrder preserving non-group items
    const newLayoutOrder = [...layout.layoutOrder];
    // Replace old group order with new order
    const groupPositions = newLayoutOrder
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => isGroupLayoutItem(item));

    // Put new group IDs at the old positions
    groupPositions.forEach(({ idx }, i) => {
      if (i < newOrder.length) {
        newLayoutOrder[idx] = newOrder[i];
      }
    });

    setLayout(prev => prev ? { ...prev, layoutOrder: newLayoutOrder } : prev);

    try {
      await reorderLayout(user.uid, newLayoutOrder, layout);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to reorder groups', description: 'Please try again.' });
      fetchData();
    }
  };

  // --- Available groups for "Add to group" dropdown ---
  const availableGroups = useMemo(() =>
    groups.map(g => ({ id: g.id, name: g.name })),
    [groups]
  );

  return (
    <div className="space-y-12 p-8">
        <header className="rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-slate-900/[0.04] bg-[bottom_1px_center] dark:bg-grid-slate-400/[0.05] dark:bg-bottom_1px_center"></div>
            <div className="relative">
                <h1 className="text-4xl md:text-5xl font-headline font-bold text-primary tracking-tight">
                Welcome to StoryStudio
                </h1>
                <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                Your AI-powered partner for crafting compelling stories, from blurb to prose. Let&apos;s begin a new chapter today.
                </p>
            </div>
        </header>

      {user && (
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-headline font-semibold">My Projects</h2>
            <div className="flex items-center gap-2">
                <Button onClick={handleOpenCreateGroupDialog} variant="outline">
                    <FolderPlus className="mr-2 h-4 w-4" />
                    New Group
                </Button>
                <Button onClick={() => setIsNewProjectOpen(true)}>
                    <Plus className="mr-2" />
                    New Project
                </Button>
            </div>
          </div>

          {!isInitialized ? (
            <div className="space-y-4">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : allProjects.length === 0 ? (
            <Card className="text-center py-12 border-dashed">
              <CardHeader>
                <div className="mx-auto bg-secondary rounded-full p-3 w-fit">
                  <BookOpen className="h-8 w-8 text-muted-foreground" />
                </div>
                <CardTitle className="mt-4">No Projects Yet</CardTitle>
                <CardDescription>
                  Start your next masterpiece by creating a new project.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => setIsNewProjectOpen(true)}>
                  <Plus className="mr-2" />
                  Create Your First Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Groups Section */}
              {groups.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleGroupDragEnd}
                >
                  <SortableContext
                    items={groupSortableIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {groups.map((group) => {
                        const groupProjects = group.projectIds
                          .map(pid => projectMap.get(pid))
                          .filter((p): p is Partial<Project> => !!p);

                        return (
                          <ProjectGroupContainer
                            key={group.id}
                            group={group}
                            projects={groupProjects}
                            ungroupedProjects={ungroupedProjects}
                            sortableId={toGroupLayoutId(group.id)}
                            onToggleCollapse={handleToggleCollapse}
                            onRenameGroup={handleOpenRenameDialog}
                            onDeleteGroup={handleDeleteGroup}
                            onReorderProjects={handleReorderInGroup}
                            onRemoveProject={handleRemoveFromGroup}
                            onAddProject={handleAddToGroup}
                            onCreateProject={(groupId) => {
                              setNewProjectTargetGroupId(groupId);
                              setIsNewProjectOpen(true);
                            }}
                            onDeleteProject={handleOpenDeleteDialog}
                            onDuplicateProject={handleOpenDuplicateDialog}
                            onTransferProject={handleOpenTransferDialog}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Ungrouped Projects */}
              {ungroupedProjects.length > 0 && (
                <div>
                  {groups.length > 0 && (
                    <h3 className="text-sm font-semibold text-muted-foreground mb-4">
                      Ungrouped Projects
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ungroupedProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onDelete={handleOpenDeleteDialog}
                        onDuplicate={handleOpenDuplicateDialog}
                        onTransfer={handleOpenTransferDialog}
                        availableGroups={availableGroups}
                        onAddToGroup={handleAddToGroup}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <footer className="text-center py-8 text-muted-foreground text-sm">
        <p>Built with imagination.</p>
      </footer>

      {/* Dialogs */}
      <NewProjectDialog
        isOpen={isNewProjectOpen}
        onOpenChange={(open) => {
            if (!open) {
              fetchData();
              setNewProjectTargetGroupId(null);
            }
            setIsNewProjectOpen(open);
        }}
        targetGroupId={newProjectTargetGroupId}
        onAddToGroup={handleAddToGroup}
      />
      {projectToDuplicate && (
        <DuplicateProjectDialog
          isOpen={isDuplicateProjectOpen}
          onOpenChange={(open) => {
              if (!open) fetchData();
              setIsDuplicateProjectOpen(open);
          }}
          sourceProjectId={projectToDuplicate.id!}
        />
      )}
      {projectToDelete && (
          <DeleteProjectDialog
            isOpen={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onConfirm={handleDeleteProject}
            projectName={projectToDelete.title || ''}
          />
      )}
      {projectToTransfer && (
        <TransferProjectDialog
          isOpen={isTransferDialogOpen}
          onOpenChange={setIsTransferDialogOpen}
          projectId={projectToTransfer.id!}
          projectName={projectToTransfer.title || ''}
          onTransferred={handleTransferred}
        />
      )}
      <GroupNameDialog
        isOpen={isGroupDialogOpen}
        onOpenChange={setIsGroupDialogOpen}
        mode={groupDialogMode}
        initialName={groupToRename?.name || ''}
        onConfirm={groupDialogMode === 'create' ? handleCreateGroup : handleRenameGroup}
      />
    </div>
  );
}
